'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function DebtConsolidationCalculator() {
  const t = useTranslations("tools.debtConsolidationCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [visibleDebts, setVisibleDebts] = useState(3);
  const [debts, setDebts] = useState([
    { name: 'Credit Card 1', balance: 5000, rate: 18.99, payment: 150 },
    { name: 'Credit Card 2', balance: 3000, rate: 21.99, payment: 90 },
    { name: 'Personal Loan', balance: 8000, rate: 12.5, payment: 250 },
    { name: '', balance: 0, rate: 0, payment: 0 },
    { name: '', balance: 0, rate: 0, payment: 0 },
    { name: '', balance: 0, rate: 0, payment: 0 },
    { name: '', balance: 0, rate: 0, payment: 0 },
    { name: '', balance: 0, rate: 0, payment: 0 },
  ]);
  const [consolidation, setConsolidation] = useState({
    rate: 8.5,
    term: 60,
    termUnit: 'months',
    fee: 2,
  });
  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    blue: '#2563eb',
    blueLight: '#3b82f6',
    green: '#22c55e',
    greenLight: '#4ade80',
    yellow: '#f59e0b',
    yellowLight: '#fbbf24',
  };

  const handleDebtChange = (index: number, field: string, value: any) => {
    const newDebts = [...debts];
    newDebts[index] = { ...newDebts[index], [field]: value };
    setDebts(newDebts);
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const activeDebts = debts.filter(d => d.balance > 0);
    if (activeDebts.length === 0) {
      alert(t('noDebtsError'));
      return;
    }

    const totalDebt = activeDebts.reduce((sum, d) => sum + d.balance, 0);
    const totalMonthlyPayment = activeDebts.reduce((sum, d) => sum + d.payment, 0);

    let totalInterestCurrent = 0;
    let monthsToPayoffCurrent = 0;

    activeDebts.forEach(debt => {
      const monthlyRate = debt.rate / 100 / 12;
      if (debt.payment > debt.balance * monthlyRate) {
        const months = Math.log(debt.payment / (debt.payment - debt.balance * monthlyRate)) / Math.log(1 + monthlyRate);
        const totalPaid = debt.payment * months;
        totalInterestCurrent += totalPaid - debt.balance;
        monthsToPayoffCurrent = Math.max(monthsToPayoffCurrent, months);
      } else {
        monthsToPayoffCurrent = 999;
        totalInterestCurrent += debt.balance * 2;
      }
    });

    const termMonths = consolidation.termUnit === 'years' ? consolidation.term * 12 : consolidation.term;
    const consolidationAmount = totalDebt * (1 + consolidation.fee / 100);
    const monthlyRate = consolidation.rate / 100 / 12;
    const consolidationPayment = consolidationAmount * (monthlyRate * Math.pow(1 + monthlyRate, termMonths)) / (Math.pow(1 + monthlyRate, termMonths) - 1);
    const totalPaidConsolidation = consolidationPayment * termMonths;
    const totalInterestConsolidation = totalPaidConsolidation - totalDebt;

    const monthlySavings = totalMonthlyPayment - consolidationPayment;
    const interestSavings = totalInterestCurrent - totalInterestConsolidation;
    const timeSavings = monthsToPayoffCurrent - termMonths;

    setResults({
      current: {
        totalDebt,
        monthlyPayment: totalMonthlyPayment,
        totalInterest: totalInterestCurrent,
        monthsToPayoff: monthsToPayoffCurrent,
        totalPaid: totalDebt + totalInterestCurrent,
      },
      consolidation: {
        loanAmount: consolidationAmount,
        monthlyPayment: consolidationPayment,
        totalInterest: totalInterestConsolidation,
        monthsToPayoff: termMonths,
        totalPaid: totalPaidConsolidation,
        fee: consolidationAmount - totalDebt,
      },
      savings: {
        monthly: monthlySavings,
        interest: interestSavings,
        time: timeSavings,
      },
      recommended: interestSavings > 0 && monthlySavings > 0,
    });
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, maxWidth: '800px', margin: '0 auto' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const,
      borderRadius: '1rem 1rem 0 0',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolTitleStyle = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '1.8rem' }
  );

  const sectionTitleStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.blue, margin: '0 0 1.5rem 0', paddingBottom: '0.5rem', borderBottom: `3px solid ${colors.blue}` },
    { fontSize: '1.2rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.3rem' }
  );

  const tableContainerStyle: React.CSSProperties = {
    width: '100%',
    overflowX: 'auto',
    marginBottom: '2rem',
  };

  const tableStyle = getResponsiveStyle(
    { width: '100%', borderCollapse: 'collapse' as const },
    { fontSize: '0.85rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.9rem' }
  );

  const thStyle = getResponsiveStyle(
    { background: '#f1f5f9', textAlign: 'left' as const, fontWeight: 600, color: '#475569', borderBottom: '2px solid #cbd5e1' },
    { padding: '0.5rem 0.25rem' },
    { padding: '0.75rem 0.5rem' },
    { padding: '0.75rem 0.5rem' }
  );

  const tdStyle = getResponsiveStyle(
    { borderBottom: '1px solid #e2e8f0' },
    { padding: '0.5rem 0.25rem' },
    { padding: '0.5rem' },
    { padding: '0.5rem' }
  );

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #cbd5e1',
    borderRadius: '0.375rem',
    fontSize: '0.9rem',
    transition: 'all 0.2s',
  };

  const showMoreBtnStyle = getResponsiveStyle(
    {
      background: '#f1f5f9',
      color: colors.blue,
      border: '2px dashed #cbd5e1',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      fontWeight: 600,
      margin: '1rem 0 2rem 0',
      transition: 'all 0.2s',
      width: '100%',
    },
    { padding: '0.75rem 1rem', fontSize: '0.9rem' },
    { padding: '0.75rem 1.5rem', fontSize: '1rem' },
    { padding: '0.75rem 1.5rem', fontSize: '1rem' }
  );

  const consolidationSectionStyle = getResponsiveStyle(
    {
      background: '#fef3c7',
      borderRadius: '0.75rem',
      marginBottom: '2rem',
      border: '2px solid #fbbf24',
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const formRowStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginBottom: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const formGroupStyle: React.CSSProperties = {
    marginBottom: '1rem',
  };

  const labelStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const formInputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #cbd5e1',
      borderRadius: '0.5rem',
      transition: 'all 0.2s',
    },
    { padding: '0.625rem', fontSize: '0.9rem' },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.75rem', fontSize: '1rem' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      color: 'white',
      border: 'none',
      fontWeight: 700,
      borderRadius: '0.75rem',
      cursor: 'pointer',
      transition: 'all 0.3s',
      boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)',
      width: '100%',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' },
    { padding: '1rem 3rem', fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderTop: '4px solid #22c55e',
      marginTop: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { color: '#15803d', margin: '0 0 1.5rem 0', textAlign: 'center' as const },
    { fontSize: '1.3rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const comparisonGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const comparisonCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const cardTitleStyle = getResponsiveStyle(
    { fontWeight: 700, marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #e5e7eb' },
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' },
    { fontSize: '1.2rem' }
  );

  const metricRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 0',
    borderBottom: '1px solid #f3f4f6',
  };

  const metricLabelStyle = getResponsiveStyle(
    { color: '#6b7280', fontWeight: 500 },
    { fontSize: '0.85rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.9rem' }
  );

  const metricValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary },
    { fontSize: '1rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.1rem' }
  );

  const savingsCardStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderRadius: '0.75rem',
      border: '2px solid #fbbf24',
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const savingsGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(3, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' }
  );

  const savingsItemStyle: React.CSSProperties = {
    textAlign: 'center',
  };

  const savingsLabelStyle = getResponsiveStyle(
    { color: '#92400e', fontWeight: 600, marginBottom: '0.5rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.9rem' }
  );

  const savingsValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#78350f' },
    { fontSize: '1.3rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const recommendationStyle = getResponsiveStyle(
    {
      borderRadius: '0.5rem',
      textAlign: 'center' as const,
      fontWeight: 600,
      marginTop: '1.5rem',
    },
    { padding: '0.875rem', fontSize: '0.95rem' },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const faqItemStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.8)',
      borderRadius: '0.75rem',
      marginBottom: '1rem',
      border: '1px solid rgba(255, 200, 180, 0.3)',
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.primary, marginBottom: '0.75rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const refLinkStyle: React.CSSProperties = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word',
    transition: 'color 0.2s',
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolHeaderStyle}>
          <h2 style={toolTitleStyle}>{t('toolTitle')}</h2>
        </div>

        <form onSubmit={handleCalculate} style={{ padding: screenSize === 'mobile' ? '0' : '0 1rem' }}>
          <div style={sectionTitleStyle}>{t('currentDebtsTitle')}</div>

          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>{t('debtName')}</th>
                  <th style={thStyle}>{t('balance')}</th>
                  <th style={thStyle}>{t('interestRate')}</th>
                  <th style={thStyle}>{t('monthlyPayment')}</th>
                </tr>
              </thead>
              <tbody>
                {debts.slice(0, visibleDebts).map((debt, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>
                      <span style={{ fontWeight: 600, color: '#64748b' }}>{index + 1}</span>
                    </td>
                    <td style={tdStyle}>
                      <input
                        type="text"
                        value={debt.name}
                        onChange={(e) => handleDebtChange(index, 'name', e.target.value)}
                        placeholder={t('debtNamePlaceholder')}
                        style={inputStyle}
                      />
                    </td>
                    <td style={tdStyle}>
                      <input
                        type="number"
                        value={debt.balance || ''}
                        onChange={(e) => handleDebtChange(index, 'balance', parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        style={inputStyle}
                        step="0.01"
                      />
                    </td>
                    <td style={tdStyle}>
                      <input
                        type="number"
                        value={debt.rate || ''}
                        onChange={(e) => handleDebtChange(index, 'rate', parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        style={inputStyle}
                        step="0.01"
                      />
                    </td>
                    <td style={tdStyle}>
                      <input
                        type="number"
                        value={debt.payment || ''}
                        onChange={(e) => handleDebtChange(index, 'payment', parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        style={inputStyle}
                        step="0.01"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {visibleDebts < debts.length && (
            <button
              type="button"
              onClick={() => setVisibleDebts(Math.min(visibleDebts + 2, debts.length))}
              style={showMoreBtnStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#e0f2fe';
                e.currentTarget.style.borderColor = colors.blue;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#f1f5f9';
                e.currentTarget.style.borderColor = '#cbd5e1';
              }}
            >
              {t('showMoreDebts')}
            </button>
          )}

          <div style={consolidationSectionStyle}>
            <div style={sectionTitleStyle}>{t('consolidationLoanTitle')}</div>

            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('consolidationRate')}</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="number"
                    value={consolidation.rate}
                    onChange={(e) => setConsolidation({ ...consolidation, rate: parseFloat(e.target.value) || 0 })}
                    style={formInputStyle}
                    step="0.01"
                  />
                  <span style={{ fontWeight: 600, color: '#78350f' }}>%</span>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('loanTerm')}</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="number"
                    value={consolidation.term}
                    onChange={(e) => setConsolidation({ ...consolidation, term: parseInt(e.target.value) || 0 })}
                    style={{ ...formInputStyle, flex: 1 }}
                  />
                  <select
                    value={consolidation.termUnit}
                    onChange={(e) => setConsolidation({ ...consolidation, termUnit: e.target.value })}
                    style={{ ...formInputStyle, width: screenSize === 'mobile' ? '100px' : '120px' }}
                  >
                    <option value="months">{t('months')}</option>
                    <option value="years">{t('years')}</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>{t('originationFee')}</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="number"
                  value={consolidation.fee}
                  onChange={(e) => setConsolidation({ ...consolidation, fee: parseFloat(e.target.value) || 0 })}
                  style={formInputStyle}
                  step="0.01"
                />
                <span style={{ fontWeight: 600, color: '#78350f' }}>%</span>
              </div>
            </div>
          </div>

          <button
            type="submit"
            style={calculateBtnStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(245, 158, 11, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(245, 158, 11, 0.3)';
            }}
          >
            {t('calculateButton')}
          </button>
        </form>

        {results && (
          <div style={resultsStyle}>
            <h3 style={resultsH3Style}>{t('resultsTitle')}</h3>

            <div style={comparisonGridStyle}>
              <div style={comparisonCardStyle}>
                <div style={{ ...cardTitleStyle, color: '#dc2626' }}>{t('currentSituation')}</div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>{t('totalDebt')}</span>
                  <span style={metricValueStyle}>${results.current.totalDebt.toFixed(2)}</span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>{t('monthlyPayment')}</span>
                  <span style={metricValueStyle}>${results.current.monthlyPayment.toFixed(2)}</span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>{t('totalInterest')}</span>
                  <span style={metricValueStyle}>${results.current.totalInterest.toFixed(2)}</span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>{t('payoffTime')}</span>
                  <span style={metricValueStyle}>
                    {results.current.monthsToPayoff > 500 ? t('never') : `${Math.round(results.current.monthsToPayoff)} ${t('monthsUnit')}`}
                  </span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>{t('totalPaid')}</span>
                  <span style={metricValueStyle}>${results.current.totalPaid.toFixed(2)}</span>
                </div>
              </div>

              <div style={comparisonCardStyle}>
                <div style={{ ...cardTitleStyle, color: colors.green }}>{t('withConsolidation')}</div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>{t('loanAmount')}</span>
                  <span style={metricValueStyle}>${results.consolidation.loanAmount.toFixed(2)}</span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>{t('monthlyPayment')}</span>
                  <span style={metricValueStyle}>${results.consolidation.monthlyPayment.toFixed(2)}</span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>{t('totalInterest')}</span>
                  <span style={metricValueStyle}>${results.consolidation.totalInterest.toFixed(2)}</span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>{t('payoffTime')}</span>
                  <span style={metricValueStyle}>{results.consolidation.monthsToPayoff} {t('monthsUnit')}</span>
                </div>
                <div style={metricRowStyle}>
                  <span style={metricLabelStyle}>{t('totalPaid')}</span>
                  <span style={metricValueStyle}>${results.consolidation.totalPaid.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div style={savingsCardStyle}>
              <div style={{ ...cardTitleStyle, color: '#78350f', textAlign: 'center' }}>{t('potentialSavings')}</div>
              <div style={savingsGridStyle}>
                <div style={savingsItemStyle}>
                  <div style={savingsLabelStyle}>{t('monthlySavings')}</div>
                  <div style={savingsValueStyle}>
                    ${Math.abs(results.savings.monthly).toFixed(2)}
                    {results.savings.monthly < 0 && <span style={{ fontSize: '0.8em', color: '#dc2626' }}> ↑</span>}
                  </div>
                </div>
                <div style={savingsItemStyle}>
                  <div style={savingsLabelStyle}>{t('interestSavings')}</div>
                  <div style={savingsValueStyle}>
                    ${Math.abs(results.savings.interest).toFixed(2)}
                    {results.savings.interest < 0 && <span style={{ fontSize: '0.8em', color: '#dc2626' }}> ↑</span>}
                  </div>
                </div>
                <div style={savingsItemStyle}>
                  <div style={savingsLabelStyle}>{t('timeSavings')}</div>
                  <div style={savingsValueStyle}>
                    {Math.abs(Math.round(results.savings.time))} {t('monthsUnit')}
                    {results.savings.time < 0 && <span style={{ fontSize: '0.8em', color: '#dc2626' }}> ↑</span>}
                  </div>
                </div>
              </div>
            </div>

            <div
              style={{
                ...recommendationStyle,
                background: results.recommended ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)' : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                color: results.recommended ? '#065f46' : '#991b1b',
                border: results.recommended ? '2px solid #10b981' : '2px solid #ef4444',
              }}
            >
              {results.recommended ? `✓ ${t('recommendationYes')}` : `✗ ${t('recommendationNo')}`}
            </div>
          </div>
        )}
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight5')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight6')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.2rem' : '1.4rem', marginTop: '1.5rem' }}>{t('benefitsTitle')}</h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('benefit1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('benefit2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('benefit3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('benefit4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('benefit5')}</li>
        </ul>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.2rem' : '1.4rem', marginTop: '1.5rem' }}>{t('considerationsTitle')}</h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('consideration1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('consideration2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('consideration3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('consideration4')}</li>
        </ul>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.2rem' : '1.4rem', marginTop: '1.5rem' }}>{t('alternativesTitle')}</h3>
        <p style={paragraphStyle}>{t('alternativesParagraph')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq1Question')}</div>
          <div style={faqAnswerStyle}>{t('faq1Answer')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq2Question')}</div>
          <div style={faqAnswerStyle}>{t('faq2Answer')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq3Question')}</div>
          <div style={faqAnswerStyle}>{t('faq3Answer')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq4Question')}</div>
          <div style={faqAnswerStyle}>{t('faq4Answer')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq5Question')}</div>
          <div style={faqAnswerStyle}>{t('faq5Answer')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq6Question')}</div>
          <div style={faqAnswerStyle}>{t('faq6Answer')}</div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>
            <a
              href="https://www.incharge.org/tools-resources/personal-finance-calculators/debt-consolidation-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              Debt Consolidation Calculator - InCharge
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a
              href="https://www.discover.com/personal-loans/debt-consolidation-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              Debt Consolidation Rate Calculator - Discover
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a
              href="https://www.experian.com/blogs/ask-experian/pros-and-cons-of-debt-consolidation/"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              Pros and Cons of Debt Consolidation - Experian
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a
              href="https://www.nerdwallet.com/personal-loans/learn/what-is-debt-consolidation"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              What Is Debt Consolidation - NerdWallet
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a
              href="https://www.pnc.com/insights/personal-finance/borrow/what-is-debt-consolidation-and-how-does-it-work.html"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              What Is Debt Consolidation & How Does It Work - PNC Insights
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}